import { supabase } from '../utils/supabase';

/**
 * Service for handling user reports (jobs, users, messages, content)
 */
class ReportService {
  
  /**
   * Submit a job report
   * @param {Object} reportData - Report data
   * @param {string} reportData.jobId - ID of the job being reported
   * @param {string} reportData.reportedBy - ID of the user making the report
   * @param {string} reportData.reason - Reason for the report
   * @param {string} reportData.description - Additional details (optional)
   * @returns {Promise<Object>} Result object with success status and data/error
   */
  async submitJobReport({ jobId, reportedBy, reason, description = '' }) {
    try {
      if (!jobId || !reportedBy || !reason) {
        return {
          success: false,
          error: 'Missing required fields: jobId, reportedBy, and reason are required'
        };
      }

      const reportData = {
        type: 'job',
        reported_job: jobId,
        reported_by: reportedBy,
        reason: reason.trim(),
        description: description.trim(),
        status: 'submitted', // Set to submitted so it appears in admin dashboard
        priority: 'medium',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('reports')
        .insert([reportData])
        .select(`
          id,
          type,
          reason,
          description,
          status,
          created_at,
          job:jobs(id, title, company_name)
        `)
        .single();

      if (error) {
        console.error('Error submitting job report:', error);
        return {
          success: false,
          error: error.message || 'Failed to submit report'
        };
      }

      return {
        success: true,
        data: data,
        message: 'Report submitted successfully. Our team will review it shortly.'
      };

    } catch (error) {
      console.error('Error in submitJobReport:', error);
      return {
        success: false,
        error: error.message || 'An unexpected error occurred while submitting the report'
      };
    }
  }

  /**
   * Submit a user report
   * @param {Object} reportData - Report data
   * @param {string} reportData.userId - ID of the user being reported
   * @param {string} reportData.reportedBy - ID of the user making the report
   * @param {string} reportData.reason - Reason for the report
   * @param {string} reportData.description - Additional details (optional)
   * @returns {Promise<Object>} Result object with success status and data/error
   */
  async submitUserReport({ userId, reportedBy, reason, description = '' }) {
    try {
      if (!userId || !reportedBy || !reason) {
        return {
          success: false,
          error: 'Missing required fields: userId, reportedBy, and reason are required'
        };
      }

      const reportData = {
        type: 'user',
        reported_user: userId,
        reported_by: reportedBy,
        reason: reason.trim(),
        description: description.trim(),
        status: 'submitted',
        priority: 'medium',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('reports')
        .insert([reportData])
        .select(`
          id,
          type,
          reason,
          description,
          status,
          created_at,
          reported_user_profile:user_profiles!reports_reported_user_fkey(id, full_name, email)
        `)
        .single();

      if (error) {
        console.error('Error submitting user report:', error);
        return {
          success: false,
          error: error.message || 'Failed to submit report'
        };
      }

      return {
        success: true,
        data: data,
        message: 'Report submitted successfully. Our team will review it shortly.'
      };

    } catch (error) {
      console.error('Error in submitUserReport:', error);
      return {
        success: false,
        error: error.message || 'An unexpected error occurred while submitting the report'
      };
    }
  }

  /**
   * Submit a message report
   * @param {Object} reportData - Report data  
   * @param {string} reportData.messageId - ID of the message being reported
   * @param {string} reportData.reportedBy - ID of the user making the report
   * @param {string} reportData.reason - Reason for the report
   * @param {string} reportData.description - Additional details (optional)
   * @returns {Promise<Object>} Result object with success status and data/error
   */
  async submitMessageReport({ messageId, reportedBy, reason, description = '' }) {
    try {
      if (!messageId || !reportedBy || !reason) {
        return {
          success: false,
          error: 'Missing required fields: messageId, reportedBy, and reason are required'
        };
      }

      const reportData = {
        type: 'message',
        reported_message: messageId,
        reported_by: reportedBy,
        reason: reason.trim(),
        description: description.trim(),
        status: 'submitted',
        priority: 'high', // Messages usually get higher priority
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('reports')
        .insert([reportData])
        .select(`
          id,
          type,
          reason,
          description,
          status,
          created_at
        `)
        .single();

      if (error) {
        console.error('Error submitting message report:', error);
        return {
          success: false,
          error: error.message || 'Failed to submit report'
        };
      }

      return {
        success: true,
        data: data,
        message: 'Report submitted successfully. Our team will review it shortly.'
      };

    } catch (error) {
      console.error('Error in submitMessageReport:', error);
      return {
        success: false,
        error: error.message || 'An unexpected error occurred while submitting the report'
      };
    }
  }

  /**
   * Check if a user has already reported a specific item
   * @param {string} userId - ID of the user
   * @param {string} itemId - ID of the item (job, user, message)
   * @param {string} itemType - Type of item ('job', 'user', 'message')
   * @returns {Promise<Object>} Result object with success status and hasReported boolean
   */
  async checkExistingReport(userId, itemId, itemType) {
    try {
      if (!userId || !itemId || !itemType) {
        return {
          success: false,
          error: 'Missing required parameters'
        };
      }

      let query = supabase
        .from('reports')
        .select('id, status, created_at')
        .eq('reported_by', userId);

      // Add the appropriate filter based on item type
      switch (itemType.toLowerCase()) {
        case 'job':
          query = query.eq('reported_job', itemId);
          break;
        case 'user':
          query = query.eq('reported_user', itemId);
          break;
        case 'message':
          query = query.eq('reported_message', itemId);
          break;
        default:
          return {
            success: false,
            error: 'Invalid item type. Must be job, user, or message'
          };
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error checking existing report:', error);
        return {
          success: false,
          error: error.message || 'Failed to check existing reports'
        };
      }

      return {
        success: true,
        hasReported: data && data.length > 0,
        existingReports: data || []
      };

    } catch (error) {
      console.error('Error in checkExistingReport:', error);
      return {
        success: false,
        error: error.message || 'An unexpected error occurred'
      };
    }
  }

  /**
   * Get user's submitted reports
   * @param {string} userId - ID of the user
   * @param {number} limit - Maximum number of reports to return (optional, default: 50)
   * @returns {Promise<Object>} Result object with success status and data/error
   */
  async getUserReports(userId, limit = 50) {
    try {
      if (!userId) {
        return {
          success: false,
          error: 'User ID is required'
        };
      }

      const { data, error } = await supabase
        .from('reports')
        .select(`
          id,
          type,
          reason,
          description,
          status,
          created_at,
          updated_at,
          job:jobs(id, title, company_name),
          reported_user_profile:user_profiles!reports_reported_user_fkey(id, full_name, email)
        `)
        .eq('reported_by', userId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error getting user reports:', error);
        return {
          success: false,
          error: error.message || 'Failed to get reports'
        };
      }

      return {
        success: true,
        data: data || []
      };

    } catch (error) {
      console.error('Error in getUserReports:', error);
      return {
        success: false,
        error: error.message || 'An unexpected error occurred'
      };
    }
  }
}

// Export singleton instance
const reportService = new ReportService();
export default reportService;